/**
 * ADR-0245 §4.1 — typed client for the Activity → Suspicious threats
 * surface. Standalone (HttpClient in, typed result out), same shape
 * as `apps/dashboard/src/api/people.ts`.
 *
 *   GET  /v1/sites/:site_id/threats
 *   GET  /v1/sites/:site_id/threats/:threat_alert_id
 *
 * The server has already role-gated IP visibility and sanitised
 * attacker-controllable strings; this client stays dumb and never
 * mutates the response.
 */

import type {
  AllowlistAddRequest,
  AllowlistListResponse,
  BlockingResponse,
  BlocklistListResponse,
  ManualBlockRequest,
  ManualBlockResponse,
  ProtectedPathsResponse,
  SecurityActionsListResponse,
  ThreatActionRequest,
  ThreatActionResult,
  ThreatCampaignDetailResponse,
  ThreatCampaignsListResponse,
  ThreatDetailResponse,
  ThreatListResponse,
  ThreatSenderDetailResponse,
  ThreatSendersListResponse,
  ThreatSeverity,
  ThreatSignatureClass,
} from '@sill/types/api';

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';
import { planFeatureRequiredDetails } from './plan-feature.js';

export interface ThreatsQuery {
  /** `YYYY-MM-DD`, inclusive. */
  readonly date_from?: string;
  readonly date_to?: string;
  readonly severity?: readonly ThreatSeverity[];
  readonly class?: readonly ThreatSignatureClass[];
  readonly page?: number;
  /** Search text: IP (roles that see IPs) or user agent. Senders list only. */
  readonly q?: string;
}

function sitePath(site_id: string): string {
  return `/v1/sites/${encodeURIComponent(site_id)}/threats`;
}

export async function listThreats(
  http: HttpClient,
  site_id: string,
  q: ThreatsQuery = {},
): Promise<ThreatListResponse> {
  const params = new URLSearchParams();
  if (q.date_from !== undefined) params.set('date_from', q.date_from);
  if (q.date_to !== undefined) params.set('date_to', q.date_to);
  if (q.severity !== undefined) for (const s of q.severity) params.append('severity', s);
  if (q.class !== undefined) for (const c of q.class) params.append('class', c);
  if (q.page !== undefined && q.page > 1) params.set('page', String(q.page));
  const qs = params.toString();
  const res = await http.request<ThreatListResponse>({
    method: 'GET',
    path: `${sitePath(site_id)}${qs.length > 0 ? `?${qs}` : ''}`,
  });
  if (res === undefined || !Array.isArray(res.threats)) throw new ApiError('bad_response', null);
  return res;
}

/** `null` when the threat alert isn't visible (tenant / retention). */
export async function getThreat(
  http: HttpClient,
  site_id: string,
  threat_alert_id: string,
): Promise<ThreatDetailResponse | null> {
  try {
    const res = await http.request<ThreatDetailResponse>({
      method: 'GET',
      path: `${sitePath(site_id)}/${encodeURIComponent(threat_alert_id)}`,
    });
    if (res === undefined || typeof res.threat !== 'object' || res.threat === null) {
      throw new ApiError('bad_response', null);
    }
    return res;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

/**
 * ADR-0245 §4.3 S2-lite — POST a threat action (Block / Ignore / Never
 * block). Returns the new actions-state on success. Non-2xx server
 * responses throw ApiError with the server's `error` field as `kind`.
 */
export async function postThreatAction(
  http: HttpClient,
  site_id: string,
  threat_alert_id: string,
  body: ThreatActionRequest,
): Promise<ThreatActionResult> {
  try {
    const res = await http.request<ThreatActionResult>({
      method: 'POST',
      path: `${sitePath(site_id)}/${encodeURIComponent(threat_alert_id)}/actions`,
      body,
    });
    if (res === undefined || typeof (res as { ok?: unknown }).ok !== 'boolean') {
      throw new ApiError('bad_response', null);
    }
    return res;
  } catch (err) {
    // ADR-0263 S4 — plan_feature_required denial (block_* only).
    const details = planFeatureRequiredDetails(err);
    if (details !== null) {
      return {
        ok: false,
        error: 'plan_feature_required',
        plan_name: details.plan_name,
        plan_message: details.message,
      };
    }
    throw err;
  }
}

/** List live blocks for a site (dashboard Firewall surface, S2 proper). */
export async function listBlocks(
  http: HttpClient,
  site_id: string,
): Promise<BlocklistListResponse> {
  const res = await http.request<BlocklistListResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist`,
  });
  if (res === undefined || !Array.isArray(res.entries)) throw new ApiError('bad_response', null);
  return res;
}

export async function deleteBlock(
  http: HttpClient,
  site_id: string,
  block_id: string,
): Promise<void> {
  await http.request<{ ok: true; block_id: string }>({
    method: 'DELETE',
    path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist/${encodeURIComponent(block_id)}`,
  });
}

export async function listAllowlist(
  http: HttpClient,
  site_id: string,
): Promise<AllowlistListResponse> {
  const res = await http.request<AllowlistListResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/allowlist`,
  });
  if (res === undefined || !Array.isArray(res.entries)) throw new ApiError('bad_response', null);
  return res;
}

export async function addAllowlistEntry(
  http: HttpClient,
  site_id: string,
  body: AllowlistAddRequest,
): Promise<{ ok: true; allow_id: string; broad: boolean }> {
  const res = await http.request<{ ok: true; allow_id: string; broad: boolean }>({
    method: 'POST',
    path: `/v1/sites/${encodeURIComponent(site_id)}/allowlist`,
    body,
  });
  if (res === undefined) throw new ApiError('bad_response', null);
  return res;
}

export async function deleteAllowlistEntry(
  http: HttpClient,
  site_id: string,
  allow_id: string,
): Promise<void> {
  await http.request<{ ok: true; allow_id: string }>({
    method: 'DELETE',
    path: `/v1/sites/${encodeURIComponent(site_id)}/allowlist/${encodeURIComponent(allow_id)}`,
  });
}

/**
 * Security › Trusted — "Trust my current address" pre-fills with the
 * caller's IP as the API sees it (Fly TCP peer, resolveClientIp).
 * Owner / admin / reviewer only; the server 403s otherwise. The IP
 * is returned by value; this read does not audit (the subsequent
 * POST /allowlist does).
 */
export async function getMyIp(
  http: HttpClient,
  site_id: string,
): Promise<{ readonly ip: string | null; readonly family: 4 | 6 | null }> {
  const res = await http.request<{ ip: string | null; family?: 4 | 6 | null }>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/my-ip`,
  });
  if (res === undefined) throw new ApiError('bad_response', null);
  // The family is derived server-side (never from a client header).
  // Older servers may omit it; the dashboard then falls back to a
  // string sniff (an address with ":" is IPv6).
  const ip = res.ip ?? null;
  const family: 4 | 6 | null = res.family === 4 || res.family === 6
    ? res.family
    : ip === null ? null : ip.includes(':') ? 6 : 4;
  return { ip, family };
}

/* ------------------------------------------------------------------- */
/*  Suspicious v2 (2026-10-17) — per-sender grouping + BlockState.    */
/* ------------------------------------------------------------------- */

function sendersPath(site_id: string): string {
  return `/v1/sites/${encodeURIComponent(site_id)}/threat-senders`;
}

export async function listThreatSenders(
  http: HttpClient,
  site_id: string,
  q: ThreatsQuery = {},
): Promise<ThreatSendersListResponse> {
  const params = new URLSearchParams();
  if (q.date_from !== undefined) params.set('date_from', q.date_from);
  if (q.date_to !== undefined) params.set('date_to', q.date_to);
  if (q.severity !== undefined) for (const s of q.severity) params.append('severity', s);
  if (q.class !== undefined) for (const c of q.class) params.append('class', c);
  if (q.page !== undefined && q.page > 1) params.set('page', String(q.page));
  if (q.q !== undefined && q.q.trim().length > 0) params.set('q', q.q.trim());
  const qs = params.toString();
  const res = await http.request<ThreatSendersListResponse>({
    method: 'GET',
    path: `${sendersPath(site_id)}${qs.length > 0 ? `?${qs}` : ''}`,
  });
  if (res === undefined || !Array.isArray(res.senders)) throw new ApiError('bad_response', null);
  return res;
}

export async function getThreatSender(
  http: HttpClient,
  site_id: string,
  sender_ref: string,
): Promise<ThreatSenderDetailResponse | null> {
  try {
    const res = await http.request<ThreatSenderDetailResponse>({
      method: 'GET',
      path: `${sendersPath(site_id)}/${encodeURIComponent(sender_ref)}`,
    });
    if (res === undefined || typeof res.sender !== 'object' || res.sender === null) {
      throw new ApiError('bad_response', null);
    }
    return res;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

export async function getBlocking(
  http: HttpClient,
  site_id: string,
): Promise<BlockingResponse> {
  const res = await http.request<BlockingResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/blocking`,
  });
  if (res === undefined) throw new ApiError('bad_response', null);
  return res;
}

/**
 * ADR-0248 S1 (observe-only) + S3 — Settings → Blocking → Protected
 * paths card. Trailing-7-day counts (would-refuse + refused) + top
 * paths + the current mode + Tier B + Tier C + caps + may_mutate.
 */
export async function getProtectedPaths(
  http: HttpClient,
  site_id: string,
): Promise<ProtectedPathsResponse> {
  const res = await http.request<ProtectedPathsResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/protected-paths`,
  });
  if (res === undefined) throw new ApiError('bad_response', null);
  return res;
}

/* ------------------------------------------------------------------- */
/*  ADR-0248 S2 — probe-campaign grouping.                             */
/* ------------------------------------------------------------------- */

export async function listThreatCampaigns(
  http: HttpClient,
  site_id: string,
  q: { readonly date_from?: string; readonly date_to?: string } = {},
): Promise<ThreatCampaignsListResponse> {
  const params = new URLSearchParams();
  if (q.date_from !== undefined) params.set('date_from', q.date_from);
  if (q.date_to !== undefined) params.set('date_to', q.date_to);
  const qs = params.toString();
  const path = `/v1/sites/${encodeURIComponent(site_id)}/threat-campaigns${qs.length > 0 ? `?${qs}` : ''}`;
  const res = await http.request<ThreatCampaignsListResponse>({ method: 'GET', path });
  if (res === undefined || !Array.isArray(res.campaigns)) throw new ApiError('bad_response', null);
  return res;
}

/** `null` when the campaign isn't visible (tenant / retention). */
export async function getThreatCampaign(
  http: HttpClient,
  site_id: string,
  campaign_id: string,
): Promise<ThreatCampaignDetailResponse | null> {
  try {
    const res = await http.request<ThreatCampaignDetailResponse>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/threat-campaigns/${encodeURIComponent(campaign_id)}`,
    });
    if (res === undefined || typeof res.campaign !== 'object' || res.campaign === null) {
      throw new ApiError('bad_response', null);
    }
    return res;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

/**
 * ADR-0248 S3 — flip the protected-paths mode. Owner / admin only;
 * the server 403s otherwise. A 409 install_cannot_enforce (MAJOR 2
 * 2026-10-18) means the Worker is below the enforcing version;
 * `install_version` + `min_enforcing_worker_version` ride the body
 * so the UI can tell the merchant exactly what to update to.
 */
export type PutProtectedPathsModeResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly error: 'install_cannot_enforce';
      readonly install_version: string | null;
      readonly min_enforcing_worker_version: string;
    }
  | { readonly ok: false; readonly error: 'forbidden' | 'unauthorized' | 'rate_limited' | 'not_found' | 'bad_request' };

export async function putProtectedPathsMode(
  http: HttpClient,
  site_id: string,
  mode: 'observe' | 'enforce',
): Promise<PutProtectedPathsModeResult> {
  try {
    await http.request<{ ok: true; mode: 'observe' | 'enforce' }>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/protected-paths/mode`,
      body: { mode },
    });
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'conflict' && err.server_reason === 'install_cannot_enforce') {
        const b = err.server_body as {
          readonly install_version?: unknown;
          readonly min_enforcing_worker_version?: unknown;
        } | null;
        return {
          ok: false,
          error: 'install_cannot_enforce',
          install_version: (b !== null && typeof b.install_version === 'string') ? b.install_version : null,
          min_enforcing_worker_version: (b !== null && typeof b.min_enforcing_worker_version === 'string')
            ? b.min_enforcing_worker_version : '1.10',
        };
      }
      if (err.kind === 'forbidden') return { ok: false, error: 'forbidden' };
      if (err.kind === 'unauthorized') return { ok: false, error: 'unauthorized' };
      if (err.kind === 'rate_limited') return { ok: false, error: 'rate_limited' };
      if (err.kind === 'not_found') return { ok: false, error: 'not_found' };
      if (err.kind === 'bad_request') return { ok: false, error: 'bad_request' };
    }
    throw err;
  }
}

/**
 * ADR-0248 S3 — replace the Tier B custom-paths list.
 */
export type PutProtectedPathsListResult =
  | { readonly ok: true; readonly entries: readonly string[] }
  | { readonly ok: false; readonly error: 'forbidden' | 'unauthorized' | 'rate_limited' | 'not_found' | 'bad_request' | 'cap_exceeded' }
  | { readonly ok: false; readonly error: 'invalid_pattern'; readonly index: number; readonly raw: string; readonly reason: string }
  | { readonly ok: false; readonly error: 'plan_feature_required'; readonly plan_name: string | null; readonly plan_message: string };

async function putProtectedPathsList(
  http: HttpClient,
  site_id: string,
  kind: 'custom' | 'exceptions',
  entries: readonly string[],
): Promise<PutProtectedPathsListResult> {
  try {
    const res = await http.request<{ ok: true; entries: readonly string[] }>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/protected-paths/${kind}`,
      body: { entries },
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return { ok: true, entries: res.entries };
  } catch (err) {
    // ADR-0263 S4 — plan_feature_required (custom_attack_paths).
    const details = planFeatureRequiredDetails(err);
    if (details !== null) {
      return {
        ok: false,
        error: 'plan_feature_required',
        plan_name: details.plan_name,
        plan_message: details.message,
      };
    }
    if (err instanceof ApiError && typeof err.server_reason === 'string') {
      const r = err.server_reason;
      if (r === 'invalid_pattern' && err.server_body !== undefined) {
        const b = err.server_body as {
          readonly index?: unknown;
          readonly raw?: unknown;
          readonly reason?: unknown;
        };
        return {
          ok: false,
          error: 'invalid_pattern',
          index: typeof b.index === 'number' ? b.index : 0,
          raw: typeof b.raw === 'string' ? b.raw : '',
          reason: typeof b.reason === 'string' ? b.reason : 'unknown',
        };
      }
      if (r === 'cap_exceeded' || r === 'bad_request' || r === 'forbidden'
        || r === 'unauthorized' || r === 'rate_limited' || r === 'not_found') {
        return { ok: false, error: r };
      }
    }
    throw err;
  }
}

export async function putProtectedPathsCustom(
  http: HttpClient,
  site_id: string,
  entries: readonly string[],
): Promise<PutProtectedPathsListResult> {
  return putProtectedPathsList(http, site_id, 'custom', entries);
}

export async function putProtectedPathsExceptions(
  http: HttpClient,
  site_id: string,
  entries: readonly string[],
): Promise<PutProtectedPathsListResult> {
  return putProtectedPathsList(http, site_id, 'exceptions', entries);
}

/**
 * Founder bug 2026-10-18 (block-state consistency): bulk per-address
 * lookup. The Person / Bot / Scanner surfaces call this on open +
 * after every Block/Unblock/Trust so the primary action can swap for
 * a "Blocked until …" chip when the address is already covered.
 * CIDR-aware server-side: a block on a /64 covering the visitor's
 * latest IP counts as blocked for the exact IP too.
 */
export interface BlockLookupHit {
  readonly ip: string;
  readonly blocked: true;
  readonly block_id: string;
  readonly target_display: string;
  readonly target_kind: 'ip' | 'cidr';
  readonly expires_at: string | null;
  readonly created_at: string;
}
export interface BlockLookupMiss {
  readonly ip: string;
  readonly blocked: false;
}
export type BlockLookup = BlockLookupHit | BlockLookupMiss;

export async function getBlocklistLookup(
  http: HttpClient,
  site_id: string,
  ips: readonly string[],
): Promise<{ readonly lookups: readonly BlockLookup[] }> {
  const unique = Array.from(new Set(ips));
  if (unique.length === 0) return { lookups: [] };
  const res = await http.request<{ lookups: readonly BlockLookup[] }>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist/lookup?ips=${unique.map(encodeURIComponent).join(',')}`,
  });
  if (res === undefined) return { lookups: [] };
  return res;
}

/**
 * Founder bug 2026-10-18 (block-state consistency): Unblock button
 * on an "already blocked" chip. DELETE
 * /v1/sites/:site_id/blocklist/:block_id lives in sites-threat-actions
 * (ADR-0245); role-checked server-side.
 */
export async function deleteBlocklistEntry(
  http: HttpClient,
  site_id: string,
  block_id: string,
): Promise<{ readonly ok: boolean; readonly error?: string }> {
  try {
    const res = await http.request<{ ok: boolean; block_id: string }>({
      method: 'DELETE',
      path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist/${encodeURIComponent(block_id)}`,
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return { ok: res.ok === true };
  } catch (err) {
    if (err instanceof ApiError
      && (err.kind === 'conflict' || err.kind === 'bad_request' || err.kind === 'not_found' || err.kind === 'forbidden' || err.kind === 'unauthorized' || err.kind === 'rate_limited')
      && typeof err.server_reason === 'string') {
      return { ok: false, error: err.server_reason };
    }
    throw err;
  }
}

/**
 * Manual block. Server returns 201 { ok:true, block, state } on success.
 * On a 409 self / shared / verified-crawler, the server sets `body.error`
 * to one of the ManualBlockErrorCode values; the HttpClient surfaces
 * that as `ApiError.server_reason`, which we fold into an ok:false body
 * so the caller branches on `error` only.
 */
export async function postManualBlock(
  http: HttpClient,
  site_id: string,
  body: ManualBlockRequest,
): Promise<ManualBlockResponse> {
  try {
    const res = await http.request<ManualBlockResponse>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist`,
      body,
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    // ADR-0263 S4 — surface the plain-copy plan name on
    // `plan_feature_required`.
    const details = planFeatureRequiredDetails(err);
    if (details !== null) {
      return {
        ok: false,
        error: 'plan_feature_required',
        plan_name: details.plan_name,
        plan_message: details.message,
      };
    }
    if (err instanceof ApiError
      && (err.kind === 'conflict' || err.kind === 'bad_request' || err.kind === 'forbidden'
        || err.kind === 'unauthorized' || err.kind === 'rate_limited'
        // 503 cloud_ranges_loading right after an API restart: the panel
        // has copy for it ("still loading network data"), so return it
        // instead of throwing into "Something went wrong".
        || err.kind === 'service_unavailable')
      && typeof err.server_reason === 'string') {
      const code = err.server_reason as import('@sill/types/api').ManualBlockErrorCode;
      return { ok: false, error: code };
    }
    throw err;
  }
}

/**
 * Suspicious v2 §3.4 — flip the per-site firewall mode. Owner / admin
 * only; the server 403s otherwise. A 409 install_cannot_enforce means
 * the Worker is at < 1.8 OR the install isn't a Worker — the UI
 * surfaces the "Update your Cloudflare Worker" / "Set up the Worker"
 * copy for that case.
 */
export type PutFirewallModeResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly error: 'install_cannot_enforce' | 'forbidden' | 'plan_feature_required' | 'unauthorized' | 'rate_limited' | 'not_found' | 'bad_request';
      /** Server-supplied upsell plan name on `plan_feature_required`
       *  (ADR-0263 S4). `null` when the catalog has no plan that
       *  includes the feature yet. */
      readonly plan_name?: string | null;
    };

export async function putFirewallMode(
  http: HttpClient,
  site_id: string,
  mode: 'observe' | 'enforce',
): Promise<PutFirewallModeResult> {
  try {
    await http.request<{ ok: true; state: unknown }>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/firewall-mode`,
      body: { mode },
    });
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'conflict' && err.server_reason === 'install_cannot_enforce') {
        return { ok: false, error: 'install_cannot_enforce' };
      }
      if (err.kind === 'forbidden' && err.server_reason === 'plan_feature_required') {
        return {
          ok: false,
          error: 'plan_feature_required',
          plan_name: readPlanName(err.server_body),
        };
      }
      if (err.kind === 'forbidden') return { ok: false, error: 'forbidden' };
      if (err.kind === 'unauthorized') return { ok: false, error: 'unauthorized' };
      if (err.kind === 'rate_limited') return { ok: false, error: 'rate_limited' };
      if (err.kind === 'not_found') return { ok: false, error: 'not_found' };
      if (err.kind === 'bad_request') return { ok: false, error: 'bad_request' };
    }
    throw err;
  }
}

/** Pull the plan name out of a `plan_feature_required` 403 body. The
 *  server shape is `{ plan: { name, ... } | null }`; null means no v2
 *  plan matches (today, with the draft catalog). */
function readPlanName(body: Record<string, unknown> | null | undefined): string | null {
  if (body === null || body === undefined) return null;
  const plan = (body as { plan?: unknown }).plan;
  if (plan === null || typeof plan !== 'object') return null;
  const name = (plan as { name?: unknown }).name;
  return typeof name === 'string' ? name : null;
}

/**
 * Suspicious v2 §5.4 — change the duration of an existing live block.
 */
export async function patchBlock(
  http: HttpClient,
  site_id: string,
  block_id: string,
  duration: '24h' | '7d' | 'forever',
): Promise<import('@sill/types/api').BlocklistPatchResponse> {
  try {
    const res = await http.request<import('@sill/types/api').BlocklistPatchResponse>({
      method: 'PATCH',
      path: `/v1/sites/${encodeURIComponent(site_id)}/blocklist/${encodeURIComponent(block_id)}`,
      body: { duration },
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    if (err instanceof ApiError && typeof err.server_reason === 'string'
      && (err.kind === 'conflict' || err.kind === 'bad_request' || err.kind === 'not_found' || err.kind === 'forbidden' || err.kind === 'unauthorized' || err.kind === 'rate_limited')) {
      return { ok: false, error: err.server_reason as import('@sill/types/api').BlocklistPatchErrorCode };
    }
    throw err;
  }
}

/**
 * Suspicious v2 §5.5 — 30-day sender-scoped Ignore across every
 * class the sender raised.
 */
export async function postSenderIgnore(
  http: HttpClient,
  site_id: string,
  sender_ref: string,
): Promise<import('@sill/types/api').SenderIgnoreResponse> {
  try {
    const res = await http.request<import('@sill/types/api').SenderIgnoreResponse>({
      method: 'POST',
      path: `/v1/sites/${encodeURIComponent(site_id)}/threat-senders/${encodeURIComponent(sender_ref)}/ignore`,
      body: {},
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    if (err instanceof ApiError && typeof err.server_reason === 'string') {
      return { ok: false, error: err.server_reason as import('@sill/types/api').SenderIgnoreErrorCode };
    }
    // The client throws a 429 as AuthRateLimitError, not ApiError.
    if (err instanceof AuthRateLimitError) return { ok: false, error: 'rate_limited' };
    throw err;
  }
}

/* ------------------------------------------------------------------- */
/*  ADR-0249 S5 — campaign auto-block client.                          */
/* ------------------------------------------------------------------- */

function autoBlockPath(site_id: string, campaign_id: string): string {
  return `/v1/sites/${encodeURIComponent(site_id)}/threat-campaigns/${encodeURIComponent(campaign_id)}/auto-block`;
}

/** GET state. `null` when the campaign isn't visible to this tenant. */
export async function getCampaignAutoblockState(
  http: HttpClient,
  site_id: string,
  campaign_id: string,
): Promise<import('@sill/types/api').CampaignAutoblockStateResponse | null> {
  try {
    const res = await http.request<import('@sill/types/api').CampaignAutoblockStateResponse>({
      method: 'GET',
      path: autoBlockPath(site_id, campaign_id),
    });
    if (res === undefined || typeof res.state !== 'object') throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** POST arm. Server returns the full new state + first-pass counts. */
export async function enableCampaignAutoblock(
  http: HttpClient,
  site_id: string,
  campaign_id: string,
): Promise<import('@sill/types/api').CampaignAutoblockEnableResponse | import('@sill/types/api').CampaignAutoblockErrorBody> {
  try {
    const res = await http.request<import('@sill/types/api').CampaignAutoblockEnableResponse>({
      method: 'POST',
      path: autoBlockPath(site_id, campaign_id),
      body: {},
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    // ADR-0263 S4 — plan_feature_required (blocking).
    const details = planFeatureRequiredDetails(err);
    if (details !== null) {
      return {
        ok: false,
        error: 'plan_feature_required',
        plan_name: details.plan_name,
        plan_message: details.message,
      };
    }
    if (err instanceof ApiError && typeof err.server_reason === 'string') {
      return {
        ok: false,
        error: err.server_reason as import('@sill/types/api').CampaignAutoblockErrorBody['error'],
      };
    }
    throw err;
  }
}

/** DELETE undo. Scoped revoke of exactly this campaign's auto-block
 *  entries + disarm. */
export async function disableCampaignAutoblock(
  http: HttpClient,
  site_id: string,
  campaign_id: string,
): Promise<import('@sill/types/api').CampaignAutoblockDisableResponse | import('@sill/types/api').CampaignAutoblockErrorBody> {
  try {
    const res = await http.request<import('@sill/types/api').CampaignAutoblockDisableResponse>({
      method: 'DELETE',
      path: autoBlockPath(site_id, campaign_id),
    });
    if (res === undefined) throw new ApiError('bad_response', null);
    return res;
  } catch (err) {
    if (err instanceof ApiError && typeof err.server_reason === 'string') {
      return {
        ok: false,
        error: err.server_reason as import('@sill/types/api').CampaignAutoblockErrorBody['error'],
      };
    }
    throw err;
  }
}

/**
 * Suspicious v2 §5.6 — attested addresses associated with an
 * agent_key, used by the bot / agent Block affordance (§4.2).
 */
/** Founder 2026-10-19 — the address behind one record (unnamed bots). */
export async function getRecordAddress(
  http: HttpClient,
  site_id: string,
  record_id: string,
): Promise<import('@sill/types/api').RecordAddressResponse> {
  const q = new URLSearchParams();
  q.set('record_id', record_id);
  const res = await http.request<import('@sill/types/api').RecordAddressResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/record-address?${q.toString()}`,
  });
  if (res === undefined || typeof res.status !== 'string') throw new ApiError('bad_response', null);
  return res;
}

export async function getSenderAddresses(
  http: HttpClient,
  site_id: string,
  agent_key: string,
): Promise<import('@sill/types/api').SenderAddressesResponse> {
  const q = new URLSearchParams();
  q.set('agent_key', agent_key);
  const res = await http.request<import('@sill/types/api').SenderAddressesResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/sender-addresses?${q.toString()}`,
  });
  if (res === undefined || !Array.isArray(res.addresses)) throw new ApiError('bad_response', null);
  return res;
}

/**
 * Security · Recent changes — site-scoped action trail. 25 rows per
 * page, newest first.
 */
export async function getSecurityActions(
  http: HttpClient,
  site_id: string,
  page = 1,
): Promise<SecurityActionsListResponse> {
  const q = new URLSearchParams();
  if (page > 1) q.set('page', String(page));
  const qs = q.toString();
  const res = await http.request<SecurityActionsListResponse>({
    method: 'GET',
    path: `/v1/sites/${encodeURIComponent(site_id)}/security/actions${qs.length > 0 ? `?${qs}` : ''}`,
  });
  if (res === undefined || !Array.isArray(res.actions)) throw new ApiError('bad_response', null);
  return res;
}
